import createClient from "openapi-fetch";
import { env } from "@/env";
import { getAccessToken, getRefreshToken, isRefreshTokenExpired } from "@/lib/auth/storage";
import { useAuthStore } from "@/stores/auth-store";
import { apiClient } from "./client";
import type { paths } from "./types";

/**
 * Authenticated API client that automatically:
 * - Adds JWT tokens to requests
 * - Refreshes access token on 401 errors
 * - Retries failed requests after refresh
 */
export const authClient = createClient<paths>({
	baseUrl: env.VITE_API_BASE_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

// Track if we're currently refreshing to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Refresh the access token using the refresh token
 */
async function refreshAccessToken(): Promise<boolean> {
	// If already refreshing, wait for that operation to complete
	if (isRefreshing && refreshPromise) {
		return refreshPromise;
	}

	isRefreshing = true;
	refreshPromise = (async () => {
		try {
			const refreshToken = getRefreshToken();

			if (!refreshToken || isRefreshTokenExpired()) {
				// Refresh token is missing or expired, log out the user
				useAuthStore.getState().clearAuth();
				return false;
			}

			const { data, error } = await apiClient.POST("/api/v1/auth/refresh", {
				body: {
					refresh_token: refreshToken,
				},
			});

			if (error || !data) {
				// Refresh failed, log out the user
				useAuthStore.getState().clearAuth();
				return false;
			}

			// Update tokens in the store
			useAuthStore.getState().setTokens({
				accessToken: data.api_key,
				refreshToken: data.refresh_token,
				accessExpiresAt: data.access_expires_at || "",
				refreshExpiresAt: data.refresh_expires_at || "",
			});

			return true;
		} catch (error) {
			console.error("[Auth] Failed to refresh access token:", error);
			import("@sentry/tanstackstart-react")
				.then((Sentry) =>
					Sentry.captureException(error, { tags: { area: "auth", action: "token_refresh" } })
				)
				.catch(() => {});
			useAuthStore.getState().clearAuth();
			return false;
		} finally {
			isRefreshing = false;
			refreshPromise = null;
		}
	})();

	return refreshPromise;
}

/**
 * Request interceptor to add auth token
 * Skips during SSR (no localStorage on server)
 */
authClient.use({
	async onRequest({ request }) {
		// Skip on server-side (no localStorage)
		if (typeof window === "undefined") {
			return request;
		}

		const accessToken = getAccessToken();

		if (accessToken) {
			request.headers.set("Authorization", `Bearer ${accessToken}`);
		}

		return request;
	},
});

/**
 * Response interceptor to handle 401 errors and refresh tokens
 * Skips during SSR (no localStorage on server)
 */
authClient.use({
	async onResponse({ response, request }) {
		// Skip on server-side (no localStorage)
		if (typeof window === "undefined") {
			return response;
		}

		// If not a 401 error, return the response as-is
		if (response.status !== 401) {
			return response;
		}

		// Skip refresh for certain endpoints
		const url = new URL(request.url);
		const skipRefreshPaths = [
			"/api/v1/auth/login",
			"/api/v1/auth/register",
			"/api/v1/auth/refresh",
		];

		if (skipRefreshPaths.some((path) => url.pathname === path)) {
			return response;
		}

		// Try to refresh the token
		const refreshed = await refreshAccessToken();

		if (!refreshed) {
			// Refresh failed, return the original 401 response
			return response;
		}

		// Refresh succeeded, retry the original request with new token
		const newAccessToken = getAccessToken();
		if (newAccessToken) {
			request.headers.set("Authorization", `Bearer ${newAccessToken}`);

			// Clone the request and retry
			const retryResponse = await fetch(request);
			return retryResponse;
		}

		return response;
	},
});
